import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Sparkles, DollarSign, CheckCircle } from "lucide-react";
import { getRecommendedServices, calculatePotentialCommission } from "@/config/affiliateLinks";

/**
 * Smart service recommendation component
 * Shows relevant services based on user's journey stage
 */
const ServiceRecommendations = ({ stage, listingPrice = 0, compact = false }) => {
  const services = getRecommendedServices(stage);
  const totalCommission = calculatePotentialCommission(services);
  
  // Calculate seller savings vs agent commission
  const agentCommission = listingPrice * 0.06; // 6% typical
  const serviceCost = services.reduce((total, s) => {
    // Estimate service costs
    const costs = {
      staging: 2000,
      cleaning: 300,
      moving: 1500,
      surveying: 600,
      inspection: 500,
      mortgage: 0,
      insurance: 0
    };
    return total + (costs[s.category] || 0);
  }, 0);
  
  const netSavings = agentCommission - serviceCost;

  if (!services || services.length === 0) return null;

  if (compact) {
    return (
      <Card className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Recommended Services</h3>
              <p className="text-sm text-gray-600">{services.length} services to maximize your sale</p>
            </div>
          </div>
          <Button 
            onClick={() => window.location.href = '/professional-services'}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700"
          >
            View Services
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 border-2 border-purple-200 shadow-lg">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Recommended for You</h3>
            <p className="text-sm text-gray-600">Services that will maximize your sale price</p>
          </div>
        </div>
      </div>

      {listingPrice > 0 && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-green-800 mb-1">
                <strong>You're saving ${agentCommission.toLocaleString()} in agent commissions!</strong>
              </p>
              <p className="text-xs text-green-700">
                Invest ${serviceCost.toLocaleString()} in professional services = <strong className="text-green-900">Net savings: ${netSavings.toLocaleString()}</strong>
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
      )}

      <div className="space-y-3">
        {services.slice(0, 3).map((service, idx) => (
          <div 
            key={idx}
            className="bg-white rounded-lg p-4 border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-semibold text-gray-900">{service.name}</h4>
                  {service.local && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-semibold">
                      DFW Local
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {getServiceDescription(service.category)}
                </p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>💰 Commission: {service.commission}</span>
                  {service.priority === 1 && (
                    <span className="text-purple-600 font-semibold">⭐ Top Pick</span>
                  )}
                </div>
              </div>
              <a
                href={service.url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-4"
              >
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Get Quote
                </Button>
              </a>
            </div>
          </div>
        ))}
      </div>

      {services.length > 3 && (
        <div className="mt-4 text-center">
          <Button
            onClick={() => window.location.href = '/professional-services'}
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            View All {services.length} Recommended Services
          </Button>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-start space-x-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">
            <strong>Why these services?</strong> Real estate agents coordinate all of these for their clients. 
            Investing in these professional services will {listingPrice > 0 ? 'add $15,000+ to your sale price' : 'maximize your sale price'} 
            while you still save thousands in commissions.
          </p>
        </div>
      </div>
    </Card>
  );
};

const getServiceDescription = (category) => {
  const descriptions = {
    staging: 'Staged homes sell 73% faster and for 5-20% more money',
    cleaning: 'Professional deep clean adds $5,000-10,000 to sale price',
    moving: 'Licensed, insured movers protect your belongings during transition',
    surveying: 'Required by most lenders and title companies for closing',
    inspection: 'Never buy without a professional inspection - protects your investment',
    mortgage: 'Pre-approved buyers close faster and more reliably',
    insurance: 'Required for closing - shop rates to save $500-1,000 annually'
  };
  return descriptions[category] || 'Professional service recommended by real estate experts';
};

export default ServiceRecommendations;
